/* ============================================================
   LAINOHA — BYOB PROGRESS TRACKER QUICKVIEW
   ============================================================

   OBJECTIF
   ------------------------------------------------------------
   Faire en sorte que le bouton "Ajouter au coffret" du Quick View
   soit traité, pour notre système Lainoha, comme le bouton normal
   situé sous une carte produit BYOB.

   IMPORTANT
   ------------------------------------------------------------
   - Ce fichier NE remplace PAS le fonctionnement natif BYOB.
   - Il NE clique PAS une deuxième fois sur un bouton.
   - Il N'ajoute PAS une deuxième unité.
   - Il mémorise seulement AVANT l'ajout :
       1. le produit;
       2. l'univers BYOB;
       3. l'étape;
       4. la collection.
   - BYOB continue ensuite son ajout normalement.

   La mémoire utilisée est EXACTEMENT la même que celle déjà
   utilisée par le bouton normal de la grille :

   cuByobProductStepMap::<univers>

   Le Tracker principal reçoit également l'index de l'étape via :

   window.cuByobQuickViewStepIndex

   ============================================================ */

(function () {
  'use strict';


  /* =========================================================
     1 — OUTILS DE NORMALISATION
  ========================================================= */

  function normalizeText(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }


  function simplifyProductText(text) {
    return normalizeText(text)
      .replace(/&/g, ' et ')
      .replace(/[’']/g, ' ')
      .replace(/[-–—|•·.,;:()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }


  /* =========================================================
     2 — MÊME CLÉ DE MÉMOIRE QUE LE SYSTÈME NORMAL
  ========================================================= */

  function getCurrentByobUniverseKey() {
    return (
      window.location.pathname
        .toLowerCase()
        .replace(/\/+$/, '') ||
      '/'
    );
  }


  function getStorageKey() {
    return (
      'cuByobProductStepMap::' +
      getCurrentByobUniverseKey()
    );
  }


  function getSavedStepMap() {
    try {
      return JSON.parse(
        sessionStorage.getItem(
          getStorageKey()
        ) || '{}'
      );
    } catch (error) {
      console.warn(
        '⚠️ Quick View : impossible de lire la mémoire BYOB.',
        error
      );

      return {};
    }
  }


  function saveStepMap(map) {
    try {
      sessionStorage.setItem(
        getStorageKey(),
        JSON.stringify(map)
      );
    } catch (error) {
      console.warn(
        '⚠️ Quick View : impossible de sauvegarder la mémoire BYOB.',
        error
      );
    }
  }


  /* =========================================================
     3 — TROUVER L'ÉTAPE BYOB ACTUELLE

     Retour :
     étape 1 = index 0
     étape 2 = index 1
     étape 3 = index 2
     etc.
  ========================================================= */

  function getCurrentStepIndex() {

    const container =
      document.querySelector(
        '.byob-circle-steps'
      );

    if (!container) {
      return -1;
    }


    const steps =
      Array.from(
        container.querySelectorAll(
          '.circle-step'
        )
      );


    if (!steps.length) {
      return -1;
    }


    /*
      PRIORITÉ 1 :
      état natif de BYOB.
    */

    let index =
      steps.findIndex(
        function (step) {

          const nativeClasses =
            Array.from(
              step.classList
            )
              .filter(
                function (className) {
                  return !className.startsWith(
                    'cu-v2-'
                  );
                }
              )
              .join(' ')
              .toLowerCase();


          if (
            nativeClasses.includes('active') ||
            nativeClasses.includes('current') ||
            nativeClasses.includes('selected')
          ) {
            return true;
          }


          if (
            step.getAttribute(
              'aria-current'
            ) === 'step' ||
            step.getAttribute(
              'aria-current'
            ) === 'true'
          ) {
            return true;
          }


          return !!step.querySelector(
            '.active, ' +
            '.current, ' +
            '.selected, ' +
            '[aria-current="step"], ' +
            '[aria-current="true"]'
          );
        }
      );


    /*
      PRIORITÉ 2 :
      état visuel de notre Tracker Lainoha.
    */

    if (index < 0) {

      index =
        steps.findIndex(
          function (step) {
            return (
              step.classList.contains(
                'cu-v2-current'
              ) ||
              step.classList.contains(
                'cu-v2-current-out-of-order'
              )
            );
          }
        );
    }


    return index;
  }


  /* =========================================================
     4 — TROUVER LE TITRE DU PRODUIT DANS LE QUICK VIEW
  ========================================================= */

  function getQuickViewProductTitle(
    quickView
  ) {

    if (!quickView) {
      return '';
    }


    const titleElement =
      quickView.querySelector(
        '.byob-quickview-title'
      ) ||
      quickView.querySelector(
        '.byob-quickview-right h1'
      ) ||
      quickView.querySelector(
        '.byob-quickview-right h2'
      ) ||
      quickView.querySelector(
        '[class*="quickview"][class*="title"]'
      );


    if (!titleElement) {
      return '';
    }


    return normalizeText(
      titleElement.textContent
    );
  }


  /* =========================================================
     5 — RETROUVER LA COLLECTION DE L'ÉTAPE

     Le système normal crée déjà des bannières :

     .cu-byob-rule-banner[data-step="2"]

     Elles contiennent :
     - le numéro d'étape;
     - le nom de collection;
     - le minimum.

     Nous utilisons donc cette même information.
  ========================================================= */

  function getCollectionConfigFromStepIndex(
    stepIndex
  ) {

    if (
      !Number.isInteger(stepIndex) ||
      stepIndex < 0
    ) {
      return null;
    }


    const stepNumber =
      stepIndex + 1;


    const banner =
      document.querySelector(
        '.cu-byob-rule-banner[data-step="' +
        stepNumber +
        '"]'
      );


    /*
      Si une bannière existe,
      nous reconstruisons exactement le même ID
      que le système de collection dynamique.
    */

    if (banner) {

      const titleElement =
        banner.querySelector(
          '.cu-rule-title'
        );


      const title =
        titleElement
          ? titleElement.textContent
              .replace(/\s+/g, ' ')
              .trim()
          : '';


      if (title) {

        const configId =
          'collection-' +
          stepNumber +
          '-' +
          simplifyProductText(title)
            .replace(/\s+/g, '-');


        return {
          id: configId,
          stepNumber: stepNumber,
          stepIndex: stepIndex,
          title: title
        };
      }
    }


    /*
      SÉCURITÉ POUR LES ANCIENNES CONFIGURATIONS FIXES.

      Ces noms correspondent à ceux déjà utilisés
      dans ton système historique.
    */

    const fixedIds = {
      1: 'coffret',
      2: 'douceur',
      3: 'ambiance',
      4: 'carte'
    };


    if (fixedIds[stepNumber]) {
      return {
        id: fixedIds[stepNumber],
        stepNumber: stepNumber,
        stepIndex: stepIndex,
        title: ''
      };
    }


    return null;
  }


  /* =========================================================
     6 — MÉMORISER LE QUICK VIEW COMME LE BOUTON NORMAL
  ========================================================= */

  function rememberQuickViewOrigin(
    quickView
  ) {

    const productTitle =
      getQuickViewProductTitle(
        quickView
      );


    const stepIndex =
      getCurrentStepIndex();


    const config =
      getCollectionConfigFromStepIndex(
        stepIndex
      );


    if (!productTitle) {
      console.warn(
        '⚠️ Quick View : titre du produit introuvable.'
      );

      return false;
    }


    if (
      !Number.isInteger(stepIndex) ||
      stepIndex < 0
    ) {
      console.warn(
        '⚠️ Quick View : étape BYOB introuvable.'
      );

      return false;
    }


    if (
      !config ||
      !config.id
    ) {
      console.warn(
        '⚠️ Quick View : collection BYOB introuvable.'
      );

      return false;
    }


    /*
      MÊME MÉMOIRE QUE LE BOUTON NORMAL.

      Exemple :

      {
        "bougie eucalyptus": "collection-2-spa-maison"
      }
    */

    const map =
      getSavedStepMap();


    map[productTitle] =
      config.id;


    saveStepMap(map);


    /*
      MÉMOIRE TEMPORAIRE POUR LE TRACKER PRINCIPAL.

      Le Tracker pourra ainsi attribuer
      le nouvel article à la bonne étape
      lorsqu'il détectera le changement
      dans le footer BYOB.
    */

    window.cuByobQuickViewStepIndex =
      stepIndex;


    console.log(
      '✅ QUICK VIEW MÉMORISÉ',
      {
        produit: productTitle,
        univers:
          getCurrentByobUniverseKey(),
        etape:
          stepIndex + 1,
        collectionId:
          config.id,
        collection:
          config.title
      }
    );


    return true;
  }


  /* =========================================================
     7 — DÉTECTER UNIQUEMENT LE BOUTON DU QUICK VIEW
  ========================================================= */

  function getQuickViewAddButton(
    target
  ) {

    if (
      !target ||
      typeof target.closest !==
        'function'
    ) {
      return null;
    }


    /*
      Sélecteur officiel connu du Quick View BYOB.
    */

    let button =
      target.closest(
        '.btn-quickview-add'
      );


    if (button) {
      return button;
    }


    /*
      SÉCURITÉ :
      si BYOB change légèrement sa classe,
      nous acceptons un bouton "Ajouter..."
      UNIQUEMENT s'il se trouve réellement
      dans le Quick View.
    */

    button =
      target.closest(
        '.byob-quickview-container button, ' +
        '.byob-quickview-container a'
      );


    if (!button) {
      return null;
    }


    const text =
      normalizeText(
        button.textContent
      );


    if (
      text.includes(
        'ajouter au coffret'
      ) ||
      text.includes(
        'ajouter au kit'
      ) ||
      text.includes(
        'add to bundle'
      )
    ) {
      return button;
    }


    return null;
  }


  /* =========================================================
     8 — CLIC QUICK VIEW

     CAPTURE = TRUE

     Très important :
     notre mémoire est écrite AVANT que BYOB
     traite lui-même l'ajout.
  ========================================================= */

  document.addEventListener(
    'click',
    function (event) {

      const button =
        getQuickViewAddButton(
          event.target
        );


      if (!button) {
        return;
      }


      const quickView =
        button.closest(
          '.byob-quickview-container'
        ) ||
        document.querySelector(
          '.byob-quickview-container'
        );


      if (!quickView) {
        console.warn(
          '⚠️ Bouton Quick View détecté, mais conteneur Quick View introuvable.'
        );

        return;
      }


      rememberQuickViewOrigin(
        quickView
      );


      /*
        IMPORTANT :

        Nous ne faisons :
        - ni preventDefault();
        - ni stopPropagation();
        - ni button.click();

        BYOB reçoit donc le clic original
        exactement comme auparavant.

        Une seule unité est ajoutée.
      */

    },
    true
  );


  /* =========================================================
     9 — SÉCURITÉ MÉMOIRE TEMPORAIRE

     Si, exceptionnellement, BYOB refuse l'ajout
     ou ne modifie pas son panier, la mémoire
     temporaire du Tracker ne doit pas rester
     active indéfiniment.

     Le Tracker principal la remet déjà à null
     après un vrai changement de panier.

     Ceci n'est donc qu'une sécurité.
  ========================================================= */

  document.addEventListener(
    'click',
    function (event) {

      const button =
        getQuickViewAddButton(
          event.target
        );


      if (!button) {
        return;
      }


      setTimeout(
        function () {

          if (
            window
              .cuByobQuickViewStepIndex !==
            null &&
            window
              .cuByobQuickViewStepIndex !==
            undefined
          ) {

            window.cuByobQuickViewStepIndex =
              null;


            console.log(
              '🧹 Mémoire temporaire Quick View nettoyée.'
            );
          }

        },
        5000
      );

    },
    true
  );


  console.log(
    '✅ BYOB Progress Tracker QuickView chargé — mémoire commune active'
  );

})();